/* eslint-disable no-underscore-dangle */
/* eslint-disable comma-dangle */
/* eslint-disable object-curly-newline */
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'react-tooltip';

import { setModal } from 'modules/_shared/redux/modalActions';
import { getUsers } from 'modules/profile/redux/userActions';
import { store } from 'redux/store';

import sizes from 'constants/sizes';

import EditPartnerModal from 'modules/partners/modals/EditPartner';
import representativeTypes from 'modules/society/components/SocietyRepresentative/representativeTypes';
import statusTypes from 'modules/society/components/SocietyRepresentative/statusTypes';
import { sindicationTypes } from 'modules/partners/constants/sindicationTypes';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import { getPartnerDetailsConfigByCorporateId } from 'modules/partners/utils/getPartnerDetailsConfigByCorporateId';
import { hasPartnersDecoupled, isPlatformAXA } from 'utils/showDetails';
import { dateFormat } from 'constants/formats';
import languages from 'constants/languages';
import { calculateCurrentAge } from 'modules/partners/utils/calculateCurrentAge';
import { formatPartnerExtraFieldValue } from 'modules/partners/utils/formatPartnerExtraFieldValue';

function PartnerPersonal({ society, partner, incorporationDate }) {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const partners = useSelector((state) =>
    hasPartnersDecoupled()
      ? state.partners
      : state.society?.actualSociety?.partners
  );
  const partnerCategories = useSelector((state) => state.partnerCategories);
  const partnerRepresentants = useSelector(
    (state) => state.partnerRepresentants
  );

  const { isAdmin, isReadOnly } = useGetPartnersSectionPermissions().data;
  const isReadOnlyUser = useSelector((state) =>
    partner?.readOnlyUsers?.includes(state.user['_id'])
  );

  const {
    showNationality,
    showSindication,
    showCivilStatus,
    showPartnerLanguage,
    showPartnerEditButton,
    showIncorporationDate,
  } = getPartnerDetailsConfigByCorporateId(society);

  const [address, setAddress] = useState();
  const [primaryEmails, setPrimaryEmails] = useState([]);
  const [secondaryEmails, setSecondaryEmails] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState('');
  const [sindication, setSindication] = useState();
  const [sindicationType, setSindicationType] = useState();

  const isEditButtonVisible =
    !isReadOnly && !isReadOnlyUser && (showPartnerEditButton || isAdmin);

  const getOtherUsers = async (users) => {
    try {
      const otherUsersList = await dispatch(
        getUsers({
          usersList: users,
        })
      );
      setPrimaryEmails(
        otherUsersList?.length ? otherUsersList.map((curr) => curr.email) : ['']
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const getReadOnlyUsers = async (users) => {
    try {
      const readOnlyUsersList = await dispatch(
        getUsers({
          usersList: users,
        })
      );
      setSecondaryEmails(
        readOnlyUsersList?.length
          ? readOnlyUsersList.map((curr) => curr.email)
          : ['']
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useEffect(() => {
    const { line1, zip, city, country } = partner.address || {};
    setAddress(
      `${line1 || ''} ${zip || ''} ${city || ''} ${
        country ? `(${country})` : ''
      }`
    );
  }, [partner.address]);

  useEffect(() => {
    if (partner?.otherUsers?.length) {
      getOtherUsers(partner.otherUsers);
    } else {
      setPrimaryEmails([]);
    }
    if (partner?.readOnlyUsers?.length) {
      getReadOnlyUsers(partner.readOnlyUsers);
    } else {
      setSecondaryEmails([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  useEffect(() => {
    if (partner?.sindication) {
      const sindication = [...partners, ...partnerRepresentants].find(
        (p) => p._id === partner.sindication
      );
      setSindication(sindication?.name);
      setSindicationType(partner.sindicationType || sindicationTypes.STANDARD);
    }
  }, [partner, partners, partnerRepresentants]);

  useEffect(() => {
    if (partner?.categories?.length) {
      const categories = partnerCategories?.filter(
        (category) =>
          partner?.categories?.includes(category?._id) && !category?.isDefault
      );
      setCategoryLabels(
        categories.map((category) => t(category?.name)).join(', ')
      );
    }
  }, [partnerCategories, partner?.categories, t]);

  return (
    <div
      className="nk-block-head"
      onClick={() => {
        if (isEditButtonVisible) {
          store.dispatch(
            setModal(
              <EditPartnerModal
                partner={partner}
                societyId={society?.['_id']}
                size={sizes.XL}
              />
            )
          );
        }
      }}
    >
      {isEditButtonVisible && (
        <div className="nk-block-head-content">
          <div className="nk-block float-right">
            <button
              type="button"
              className="btn btn-icon btn-trigger btn-tooltip"
              onClick={() => {
                store.dispatch(
                  setModal(
                    <EditPartnerModal
                      partner={partner}
                      societyId={society?.['_id']}
                      size={sizes.XL}
                    />
                  )
                );
              }}
              disabled={isReadOnly || isReadOnlyUser}
            >
              <em className="icon ni ni-edit mr-1" />
            </button>
          </div>
        </div>
      )}
      <h4 className="title">{t('PersonalInformation')}</h4>
      <p>{t('BasicPersonalInformation')}</p>
      <div className="nk-block-head nk-block-head-line" />

      <div className="profile-ud wider d-flex flex-column flex-md-row">
        <span className="profile-ud-label w-200px">{t('FullName')}</span>
        <span className="profile-ud-value text-left">
          {partner?.name || '-'}
        </span>
      </div>

      <div className="profile-ud wider d-flex flex-column flex-md-row">
        <span className="profile-ud-label w-200px">{t('Nif')}</span>
        <span className="profile-ud-value text-left">{partner.cif || '-'}</span>
      </div>

      <div className="profile-ud wider d-flex flex-column flex-md-row">
        <span className="profile-ud-label w-200px">
          {t('Telephone')} {partner?.phoneSecondary ? '(1)' : ''}
        </span>
        <span className="profile-ud-value text-left">
          {partner?.phone || '-'}
        </span>
      </div>

      {partner?.phoneSecondary && (
        <div className="profile-ud wider d-flex flex-column flex-md-row">
          <span className="profile-ud-label w-200px">{t('Telephone')} (2)</span>
          <span className="profile-ud-value text-left">
            {partner.phoneSecondary}
          </span>
        </div>
      )}

      <div className="profile-ud wider d-flex flex-column flex-md-row">
        <span className="profile-ud-label w-200px">{t('Address')}</span>
        <span className="profile-ud-value text-left">{address}</span>
      </div>

      <div className="profile-ud wider d-flex flex-column flex-md-row">
        <span className="profile-ud-label w-200px">
          {primaryEmails?.length === 0 &&
            secondaryEmails?.length === 0 &&
            t('Email')}
          {(primaryEmails?.length > 0 || secondaryEmails?.length > 0) &&
            t('Emails')}
        </span>
        <span className="profile-ud-value text-left">
          <div className="d-block">
            {partner.email}
            <span
              id="email-category-main-info"
              className="badge badge-outline badge-pill text-primary ucap mx-1 float-right"
            >
              {t('Main')}
            </span>
            <Tooltip
              anchorId="email-category-main-info"
              place="top"
              style={{
                zIndex: 9999,
              }}
            >
              {t('EmailCategoryMainInfo')}
            </Tooltip>
          </div>

          {primaryEmails?.map((email) => (
            <div className="d-block mt-1">
              <span className="profile-ud-value text-left">{email}</span>
              <span
                id="email-category-primary-info"
                className="badge badge-outline badge-pill text-primary ucap mx-1 float-right"
              >
                {t('Primary')}
              </span>
              <Tooltip
                anchorId="email-category-primary-info"
                place="top"
                style={{
                  zIndex: 9999,
                }}
              >
                {t('EmailCategoryPrimaryInfo')}
              </Tooltip>
            </div>
          ))}
          {secondaryEmails?.map((email) => (
            <div className="d-block mt-1">
              <span className="profile-ud-value text-left">{email}</span>
              <span
                id="email-category-secondary-info"
                className="badge badge-outline badge-pill text-primary ucap mx-1 float-right"
              >
                {t('Secondary')}
              </span>
              <Tooltip
                anchorId="email-category-secondary-info"
                place="top"
                style={{
                  zIndex: 9999,
                }}
              >
                {t('EmailCategorySecondaryInfo')}
              </Tooltip>
            </div>
          ))}
        </span>
      </div>

      {partner?.birthDate && (
        <>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('BirthDate')}</span>
            <span className="profile-ud-value text-left">
              {format(new Date(partner.birthDate), dateFormat)}
            </span>
          </div>

          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('Age')}</span>
            <span className="profile-ud-value text-left">
              {calculateCurrentAge(partner.birthDate)}
            </span>
          </div>
        </>
      )}

      {showIncorporationDate && (
        <div className="profile-ud wider d-flex flex-column flex-md-row">
          <span className="profile-ud-label w-200px">
            {t('IncoporationDate')}
          </span>
          <span className="profile-ud-value text-left">
            {incorporationDate || '-'}
          </span>
        </div>
      )}

      {isAdmin && (
        <div className="profile-ud wider d-flex flex-column flex-md-row">
          <span className="profile-ud-label w-200px">
            {t('CategoriesHeader')}
          </span>
          <span className="profile-ud-value text-left">
            {categoryLabels || '-'}
          </span>
        </div>
      )}

      {(showNationality || isAdmin) && (
        <div className="profile-ud wider d-flex flex-column flex-md-row">
          <span className="profile-ud-label w-200px">{t('Nationality')}</span>
          <span className="profile-ud-value text-left">
            {partner?.nationality || '-'}
          </span>
        </div>
      )}

      {(showSindication || isAdmin) && (
        <div className="profile-ud wider d-flex flex-column flex-md-row">
          <span className="profile-ud-label w-200px">{t('Sindication')}</span>
          <span className="profile-ud-value text-left">
            <div className="d-block">
              {sindication || '-'}

              {sindication && (
                <>
                  <span
                    id="sindication-type"
                    className="badge badge-outline badge-pill text-primary ucap mx-1 float-right"
                  >
                    {t(sindicationType)}
                  </span>
                  <Tooltip
                    anchorId="sindication-type"
                    place="top"
                    style={{ zIndex: 9999, maxWidth: 400 }}
                  >
                    {t('MarkAsFullSindicationInfo')}
                  </Tooltip>
                </>
              )}
            </div>
          </span>
        </div>
      )}

      {(showCivilStatus || isAdmin) && partner?.isNaturalPerson && (
        <div className="profile-ud wider d-flex flex-column flex-md-row">
          <span className="profile-ud-label w-200px">{t('CivilStatus')}</span>
          <span className="profile-ud-value text-left">
            {partner?.civilStatus ? t(partner?.civilStatus) : '-'}
          </span>
        </div>
      )}

      {!partner?.isNaturalPerson && (
        <div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">
              {t('CommercialRegister')}
            </span>
            <span className="profile-ud-value text-left">
              {partner?.legalInfo?.register || '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">
              {t('PartnerSection')}
            </span>
            <span className="profile-ud-value text-left">
              {partner?.legalInfo?.section || '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('Sheet')}</span>
            <span className="profile-ud-value text-left">
              {partner?.legalInfo?.sheet || '-'}
            </span>
          </div>

          <h6
            style={{ marginTop: '20px', marginBottom: '30px' }}
            className="title"
          >
            {t('RepresentativeInformation')}
          </h6>

          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('Name')}</span>
            <span className="profile-ud-value text-left">
              {partner?.representative?.name || '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('Nif')}</span>
            <span className="profile-ud-value text-left">
              {partner?.representative?.nif || '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('Email')}</span>
            <span className="profile-ud-value text-left">
              {partner?.representative?.email || '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">
              {t('RepresentativeDate')}
            </span>
            <span className="profile-ud-value text-left">
              {partner?.representative?.initDate
                ? format(
                    new Date(partner?.representative?.initDate),
                    'yyyy-MM-dd'
                  )
                : '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">
              {t('RevocationDate')}
            </span>
            <span className="profile-ud-value text-left">
              {partner?.representative?.endDate
                ? format(
                    new Date(partner?.representative?.endDate),
                    'yyyy-MM-dd'
                  )
                : '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">
              {t('TypeOfAuthority')}
            </span>
            <span className="profile-ud-value text-left">
              {representativeTypes?.name[partner?.representative?.authority] ||
                '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('Status')}</span>
            <span
              className={
                partner?.representative?.status &&
                `profile-ud-value text-left badge badge-dot badge-${
                  statusTypes.badge[partner?.representative?.status]
                }`
              }
            >
              {statusTypes?.name[partner?.representative?.status] || '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('Nationality')}</span>
            <span className="profile-ud-value text-left">
              {partner?.representative?.nationality || '-'}
            </span>
          </div>
          <div className="profile-ud wider d-flex flex-column flex-md-row">
            <span className="profile-ud-label w-200px">{t('Address')}</span>
            <span className="profile-ud-value text-left">
              {partner?.representative?.address || '-'}
            </span>
          </div>
        </div>
      )}

      {(showPartnerLanguage || isAdmin) && partner?.language && (
        <div className="profile-ud wider d-flex flex-column flex-md-row">
          <span className="profile-ud-label w-200px">{t('Language')}</span>
          <span className="profile-ud-value text-left">
            {languages?.[partner.language]?.name
              ? languages?.[partner.language]?.name
              : '-'}
          </span>
        </div>
      )}

      {partner?.extraFields && <hr className="divider" />}

      {partner?.extraFields
        ?.slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((field) => (
          <div
            key={field.labelKey}
            className="profile-ud wider d-flex flex-column flex-md-row"
          >
            <span className="profile-ud-label w-200px">
              {t(field.labelKey)}
            </span>
            <span className="profile-ud-value text-left">
              {formatPartnerExtraFieldValue(field, t)}
            </span>
          </div>
        ))}
    </div>
  );
}

export default PartnerPersonal;
